import { Component, Input, OnChanges, OnInit, ViewChildren } from '@angular/core';
import { BaseComponent } from '../base/base.component';
import { FormatUtilities } from '../utilities/format-utilities';
import { Student } from '../data/student';
import { Class } from '../data/class';
import { AssignmentReport } from '../data/assignment-report';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-class-assignment-report',
  templateUrl: './class-assignment-report.component.html',
  styleUrls: ['./class-assignment-report.component.scss']
})
export class ClassAssignmentReportComponent extends BaseComponent implements OnInit {
  longDateFormat = FormatUtilities.longDateFormatUtc;
  currentAssignment: AssignmentReport = new AssignmentReport();
  students: Student[];
  assignedStudents: Student[];
  class: Class;
  columnIds: string[] = [];
  columnHeaders: any = {};
  studentColumns: any = {};
  percentComplete: number = 0;
  iconClasses: string;
  loaded: { report: boolean, students: boolean, class: boolean } = { report: false, students: false, class: false };
  
  kadyChainID: string = "d4f790d8-7d4f-4bee-b85f-c60cf1553c24";

  ngOnInit() {
    this.route.params.subscribe({
      next: params => {
        this.dataService.getClassAssignmentReport(params.organizationId, params.assignmentId).subscribe({
          next: resp => {
            this.currentAssignment = resp;
            this.fixKadyChain();
            this.iconClasses = this.getIconForState(this.getStateForAssignment(this.currentAssignment));
            this.loaded.report = true;
            this.tryGenerateColumns();
          }
        });
        this.dataService.getStudents(false, params.classId).subscribe({
          next: resp => {
            this.students = resp;
            this.loaded.students = true;
            this.tryGenerateColumns();
          }
        });
        this.dataService.getClass(params.classId).subscribe({
          next: resp => {
            this.class = resp;
            this.loaded.class = true;
          }
        })
      }
    });
  }

  getStateForAssignment(assignment: AssignmentReport) {
    let today = new Date();
    let assignmentStart = new Date(assignment.StartDate);
    if (assignmentStart > today) return "upcoming";
    let assignmentEnd = new Date(assignment.EndDate);
    assignmentEnd.setDate(assignmentEnd.getDate() + 1); // assignments last until the END of EndDate
    if (assignmentEnd < today) return "completed";
    return "in-progress";
  }

  getIconForState(state: string) {
    if(state === "upcoming") return "fas fa-circle upcoming";
    if(state === "completed") return "fas fa-square completed";
    if(state === "in-progress") return "fas fa-star in-progress";
    return "fas fa-question-mark";
  }

  tryGenerateColumns() {
    if (this.currentAssignment && this.students) {
      this.generateColumns();
      this.calculatePercent();
    }
  }

  generateColumns() {
    if (this.currentAssignment.AssignedStudentIDs)
      this.assignedStudents = this.students.filter(s => this.currentAssignment.AssignedStudentIDs.some(a => s.ID == a.ID));
    else
      this.assignedStudents = this.students;

    this.assignedStudents.sort((a,b) => {
      let aLower = a.Name.toLocaleLowerCase();
      let bLower = b.Name.toLocaleLowerCase();

      if (aLower < bLower) return -1;
      if (aLower > bLower) return 1;
      return 0;
    })

    this.assignedStudents.forEach(student => {
      this.studentColumns[student.ID] = {};
    });

    if (!this.assignmentHasActivities()) return;

    this.columnIds = this.currentAssignment.ActivitySequence.Activities.map(activity => activity.AssignedActivityID);

    this.currentAssignment.ActivitySequence.Activities.forEach(activity => {
      this.columnHeaders[activity.AssignedActivityID] = activity;
    });

    this.currentAssignment.CompletedActivities.forEach(completedActivity => {
      if (completedActivity.CompletedAt) completedActivity.CompletedAt.forEach(activityCompletion => {
        this.studentColumns[activityCompletion.StudentID][completedActivity.AssignedActivityID] = activityCompletion.CompletedAt;
      });
    });
  }

  assignmentHasActivities() {
    return this.currentAssignment 
    && this.currentAssignment.ActivitySequence 
    && this.currentAssignment.ActivitySequence.Activities 
    && this.currentAssignment.ActivitySequence.Activities.length > 0;
  }

  calculatePercent() {
    if (!this.assignmentHasActivities()) return;

    const activityCount = this.currentAssignment.ActivitySequence.Activities.length;
    const studentCount = this.assignedStudents.length;
    const totalDenominator = activityCount * studentCount;

    if (totalDenominator === 0) {
      this.percentComplete = 0;
      return;
    }

    let totalNumerator = 0;

    for (let activity of this.currentAssignment.CompletedActivities) {
      if (activity.CompletedAt) totalNumerator += activity.CompletedAt.length;
    }

    this.percentComplete = Math.round(100 * (totalNumerator / totalDenominator));
  }

  columnHeader1(columnId: number): string {
    let column = this.columnHeaders[columnId];

    if (column.Level == 8) return `OB - ${column.Title}`;
    if (column.Level == 9) return `DC - ${column.Title}`;
    if (column.Level == 10) return `KC - ${column.Title}`;
    else return `L${column.Level} - ${column.Title}`;
  }

  columnHeader2(columnId: number): string {
    let column = this.columnHeaders[columnId];

    if (column.Level == 9) return `Conversation ${column.UnitNumber}`;
    if (column.Level == 10) return `L${column.UnitNumber}`;
    else return `Unit ${column.UnitNumber}`;
  }

  fixKadyChain(): void {
    let KadyChainActivities = this.currentAssignment.ActivitySequence.Activities.filter(a => this.isKadyChainID(a.ActivityID));

    if (KadyChainActivities && KadyChainActivities.length) {
      KadyChainActivities.forEach(activity => {
        activity.CategoryID = environment.CONVERSATION_CATEGORY_ID;
        activity.Level = 10;
        activity.MaxTime = 10;
        activity.MinTime = 3;
        activity.Title = "Kady Chain";
      });
    }
  }

  isKadyChainID(id: string): boolean {
    if (id == null) return false;
    return id.toUpperCase().includes(this.kadyChainID.toUpperCase());
  }
}

